"use client";

import React, { useState } from "react";
import { ArrowRight, CircleCheck as CheckCircle2, Globe, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
export default function ConsultationCTA() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  const copy = {
    vi: {
      badge: "Đồng hành cùng ROBOTO",
      heading: "Khởi đầu hành trình sáng tạo và tư duy công nghệ cho con",
      description: "Đặt lịch hẹn 1:1 cùng chuyên gia học thuật ROBOTO để đánh giá năng lực tư duy logic, trải nghiệm lắp ráp và lập trình mô hình thực tế, nhận lộ trình học tập cá nhân hóa hoàn toàn miễn phí.",
      cta: "Đăng ký đánh giá năng lực miễn phí",
      points: ["Đánh giá năng lực tư duy 1:1 cùng giáo viên chuyên trách", "Trải nghiệm trực tiếp bộ giáo cụ LEGO Education & VEX Robotics", "Xây dựng lộ trình đào tạo chuẩn quốc tế từ 4 đến 16 tuổi"]
    },
    en: {
      badge: "Start with ROBOTO",
      heading: "Begin your child's journey in robotics and engineering",
      description: "Schedule a dedicated 1-on-1 assessment with ROBOTO academic advisors to evaluate logical thinking, test real robot models, and receive a tailored learning roadmap completely free.",
      cta: "Book a free robotics assessment",
      points: ["1-on-1 analytical & logic evaluation with academic coaches", "Hands-on practice with official LEGO Education & VEX Robotics kits", "Tailored international STEM learning path for ages 4–16"]
    }
  };
  const t = copy[lang];
  return <section id="final-cta" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden border-t border-border">
      {/* Anchor target to satisfy #finalconsultationcta navigation */}
      <span id="finalconsultationcta" className="absolute -top-12 left-0 h-0 w-0 opacity-0 pointer-events-none" aria-hidden="true" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="bg-card text-card-foreground rounded-2xl border border-border shadow-lg p-6 sm:p-10 lg:p-12 relative overflow-hidden">
          {/* Subtle decorative background gradient accents using theme tokens */}
          <div className="absolute top-0 right-0 -mt-16 -mr-16 w-80 h-80 rounded-full bg-primary/10 blur-2xl pointer-events-none" aria-hidden="true" />
          <div className="absolute bottom-0 left-0 -mb-16 -ml-16 w-80 h-80 rounded-full bg-secondary/15 blur-2xl pointer-events-none" aria-hidden="true" />

          {/* Header row with Language Switcher */}
          <div className="flex items-center justify-between gap-4 mb-8 relative z-10">
            <Badge variant="outline" className="bg-accent/15 text-foreground border-accent/40 font-medium px-3 py-1 text-xs sm:text-sm rounded-full">
              <Sparkles className="size-3.5 text-accent mr-1 inline-block shrink-0" />
              {t.badge}
            </Badge>

            {/* Language toggle */}
            <div className="inline-flex items-center gap-1 p-1 bg-muted rounded-full border border-border text-xs font-medium">
              <Globe className="size-3.5 text-muted-foreground ml-1.5 mr-0.5 shrink-0" />
              <Button variant={lang === "vi" ? "default" : "ghost"} size="sm" className={`h-7 px-2.5 rounded-full text-xs font-semibold ${lang === "vi" ? "bg-primary text-primary-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`} onClick={() => setLang("vi")} type="button">
                VI
              </Button>
              <Button variant={lang === "en" ? "default" : "ghost"} size="sm" className={`h-7 px-2.5 rounded-full text-xs font-semibold ${lang === "en" ? "bg-primary text-primary-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`} onClick={() => setLang("en")} type="button">
                EN
              </Button>
            </div>
          </div>

          {/* Two-column layout: content & classroom photograph */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
            {/* Main copy and points */}
            <div className="lg:col-span-7 flex flex-col justify-between">
              <div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground leading-tight">
                  {t.heading}
                </h2>
                <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
                  {t.description}
                </p>
              </div>

              {/* Key assessment benefits */}
              <div className="mt-8 grid grid-cols-1 gap-3 border-y border-border/80 py-5">
                {t.points.map((point, index) => <div key={index} data-index={index} className="flex items-start gap-3">
                    <CheckCircle2 className="size-5 text-secondary shrink-0 mt-0.5" />
                    <span className="text-sm font-medium text-foreground/90 leading-snug">
                      {point}
                    </span>
                  </div>)}
              </div>

              {/* Call to action button */}
              <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-base shadow-md group rounded-xl">
                  <Link to="/FreeTrialContactPage">
                    <span>{t.cta}</span>
                    <ArrowRight className="size-5 transition-transform duration-200 group-hover:translate-x-1" />
                  </Link>
                </Button>
              </div>
            </div>

            {/* Supporting lab/classroom photograph */}
            <div className="lg:col-span-5 relative">
              <div className="relative rounded-xl overflow-hidden border border-border/80 shadow-md group">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_classroom_lab.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_classroom_lab.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_classroom_lab.png-wvc-sizes" alt="Students engaged in hands-on robotics and coding at ROBOTO STEM academy classroom" loading="lazy" className="w-full h-64 sm:h-80 lg:h-[380px] object-cover transition-transform duration-300 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent pointer-events-none" />
                <div className="absolute bottom-3 left-3 right-3 bg-background/90 backdrop-blur-md rounded-lg p-3 border border-border/60 text-xs font-medium text-foreground flex items-center justify-between shadow-sm">
                  <span className="text-foreground font-semibold flex items-center gap-2">
                    <span className="size-2 rounded-lg bg-secondary animate-pulse inline-block" />
                    ROBOTO Innovation Lab
                  </span>
                  <span className="text-muted-foreground text-[11px]">1:1 STEM Assessment</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}